import axios from "axios";
import { getHost } from "../reducers/utils";
import { API_BASE as PUBLIC_API_BASE } from "./publicEventsApi";
import { PART_TYPES, PRICE_UNITS } from "../components/BundleCard/bundleModel";
import { boardTypes } from "../reducers/utils";

/**
 * Schritt 2 der Eventstrecke -- Unterkunft ODER Pauschalreise
 * ==========================================================
 *
 * Beide Zweige fragen denselben Bestand ab, nur mit anderem Modus:
 *
 *   hotel   -> /bestAccommodationOfferForHotel   (nur Unterkunft, Anreise folgt in Schritt 3)
 *   package -> /bestPackageOfferForHotel         (Unterkunft und Flug in einem Vertrag)
 *
 * Es sind zwei Wege zum selben Ziel, keine Stufen: wer die Pauschalreise waehlt,
 * ueberspringt Schritt 3.
 *
 * Der Umkreis ist eine Bounding Box um die Venue. populatePost() bevorzugt eine
 * Box vor Region- und Hotel-IDs (utils.js) -- deshalb reicht es, lllat/lllng/
 * urlat/urlng zu setzen; eine Region muss der Reisende nicht auswaehlen.
 *
 * WICHTIG -- warum hier nichts umgerechnet wird: Der Provider liefert einen
 * Preis pro Person fuer den ganzen Aufenthalt. Ein daraus geteilter
 * "pro Nacht"-Preis waere eine Zahl, die so bei keinem Anbieter existiert.
 * Der Baustein zeigt deshalb, was geliefert wurde, mit der Einheit, die gilt.
 */

/** Startwert fuer den Umkreis um die Venue. */
export const STAY_RADIUS_KM = 15;

/** Grad je Kilometer in Nord-Sued-Richtung; in Ost-West-Richtung breitenabhaengig. */
const KM_PER_DEGREE_LAT = 111.32;

export const STAY_MODES = Object.freeze({
  HOTEL: "hotel",
  PACKAGE: "package",
});

/**
 * Bounding Box um einen Punkt. Bewusst eine Box und kein Kreis: die Suche
 * unten versteht nur Boxen, und die Entfernung je Treffer rechnen wir
 * anschliessend selbst -- als Kreis, wie der Reisende sie erwartet.
 */
export function boxAround(lat, lng, radiusKm) {
  const dLat = radiusKm / KM_PER_DEGREE_LAT;
  // Laengengrade ruecken zu den Polen hin zusammen; ohne cos(lat) waere die Box
  // in Nordeuropa doppelt so breit wie gewuenscht.
  const dLng = radiusKm / (KM_PER_DEGREE_LAT * Math.max(0.01, Math.cos((lat * Math.PI) / 180)));
  return {
    southWest: { lat: lat - dLat, lng: lng - dLng },
    northEast: { lat: lat + dLat, lng: lng + dLng },
  };
}

/** Luftlinie in Kilometern (Haversine) -- fuer die Entfernungsangabe am Baustein. */
export function distanceKm(aLat, aLng, bLat, bLng) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Vorbelegung: zwei Tage vor dem Event anreisen, zwei Tage danach abreisen. */
export const STAY_DAYS_BEFORE = 2;
export const STAY_DAYS_AFTER = 2;

/** Naechte zwischen zwei ISO-Tagen. Negativ gibt es nicht -- dann 0. */
export function nightsBetween(from, until) {
  const a = new Date(`${from}T12:00:00`);
  const b = new Date(`${until}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.max(0, Math.round((b - a) / 86400000));
}

/**
 * Reisezeitraum aus dem Eventdatum -- Vorbelegung, kein Zwang.
 *
 * Der Zeitraum der Suchmaske links taugt hier NICHT: dort stehen drei Monate
 * ohne Dauer, weil dort Termine gesucht werden. Sobald das Event feststeht,
 * ist der Zeitraum kein Suchkriterium mehr, sondern Ergebnis -- und die
 * Hotelsuche braucht ein konkretes An- und Abreisedatum.
 */
export function stayWindowForEvent(eventDate) {
  const day = String(eventDate || "").split(" ")[0].split("T")[0];
  const base = new Date(`${day}T12:00:00`);
  if (Number.isNaN(base.getTime())) return null;

  // Lokale Datumsteile, nicht toISOString(): das rechnet nach UTC um und
  // schoebe den Tag oestlich von Greenwich zurueck.
  const shift = (days) => {
    const d = new Date(base);
    d.setDate(d.getDate() + days);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
  };

  const from = shift(-STAY_DAYS_BEFORE);
  const until = shift(STAY_DAYS_AFTER);
  return { from, until, nights: nightsBetween(from, until), eventDay: day };
}

// Beide Zweige gehen ueber die nach HAEUSERN gruppierte Variante.
//
// Der Unterschied zu ...ForRegion ist nicht kosmetisch: ...ForRegion lieferte
// das EINE beste Angebot der Region, ...ForHotel je Haus das beste. Schritt 2
// stellt Unterkuenfte am Veranstaltungsort zur Auswahl -- dafuer braucht es
// mehrere Haeuser, nicht einen Regionssieger. Die ...ForRegion-Endpunkte sind
// im ibe_server entfernt.
//
// Nebeneffekt, der die Strecke breiter aufstellt: beide Endpunkte bedient im
// ibe_server auch Peakwork (dispatch-Schluessel "packageProduct" bzw.
// "bestAccommodationOfferForHotel"), samt Merge ueber mehrere Provider.
// Schritt 2 haengt damit nicht mehr allein an Amadeus.
/**
 * Den Veranstaltungsort in einen Ort uebersetzen, den JEDER Anbieter versteht.
 *
 * Eine Bounding Box ist ein Amadeus-Begriff. Andere Anbieter kennen sie nicht
 * und suchen damit ohne Ortsfilter weiter -- am 03.09.2026 lieferte die Suche
 * im Umkreis von Monza Hotels in Istanbul.
 *
 * Die Strecke darf aber nicht entscheiden, WER die Suche bedient. Deshalb wird
 * hier kein Anbieter ausgeschlossen, sondern der Ort in beide Raeume
 * aufgeloest: /locations/resolve-place liefert das Tripel {amadeus, giata}.
 * Jeder Anbieter nimmt sich daraus, was er versteht.
 *
 * Gesucht wird ueber den NAMEN, nicht ueber die Koordinaten des Veranstaltungs-
 * ortes: in `locations` sind die Angaben disjunkt verteilt -- die 14.123 Zeilen
 * mit Anbieter-Kennung haben keine Koordinaten, und die Zeilen mit Koordinaten
 * haben keine Kennung. Eine Umkreissuche findet dort nur das Unbrauchbare.
 */
export async function fetchVenuePlace(city, country) {
  const name = String(city || "").trim();
  if (!name) return null;

  const res = await axios.get(`${PUBLIC_API_BASE}/locations/resolve-place`, {
    params: { name, ...(country ? { country } : {}), limit: 5 },
  });
  const items = res?.data?.data?.items || [];

  // Exakter Namenstreffer geht vor -- "Monza" ist nicht "Monzambano".
  return items.find((i) => i.exact) || items[0] || null;
}

// `base` sagt, WER die Suche beantwortet:
//   ibe    -> ibe_server (Amadeus/Peakwork ueber dispatch)
//   py     -> ibe_pyserver, Suche in unserem EIGENEN Bestand
//
// Der Unterkunftszweig laeuft ueber den pyserver: gesucht wird bei uns
// (ratehawk_hotels), nicht bei einem Anbieter. Preis und Verfuegbarkeit kennt
// diese Quelle NICHT -- die Karten zeigen deshalb "auf Anfrage", bis der
// Anbieter-Call fuer Verfuegbarkeit und Preis dazukommt. Das ist Absicht und
// kein fehlendes Feature: erfundene Preise waeren schlimmer als keine.
const ENDPOINT = {
  [STAY_MODES.HOTEL]: { base: "py", path: "/ratehawk/best-accommodation-offer-for-hotel" },
  [STAY_MODES.PACKAGE]: { base: "ibe", path: "/bestPackageOfferForHotel" },
};

/**
 * Angebote fuer Schritt 2 holen.
 *
 * `body` ist der fertige populatePost()-Rumpf aus dem Slice -- der Dienst hier
 * kennt den Suchzustand nicht und soll ihn auch nicht kennen.
 *
 * Wirft mit Klartext, statt leer zurueckzukommen: "Provider nicht erreichbar"
 * und "keine freien Zimmer" sind fuer den Reisenden zwei verschiedene Dinge.
 */
export async function fetchStayOffers(mode, body) {
  const target = ENDPOINT[mode];
  if (!target) throw new Error(`Unbekannter Modus „${mode}".`);

  const url = (target.base === "py" ? PUBLIC_API_BASE : getHost()) + target.path;

  let res;
  try {
    res = await axios.post(url, body);
  } catch (err) {
    // axios meldet nur "Request failed with status code 400" -- die Ursache
    // steht im Koerper. Der ibe_server antwortet dort z.B. mit
    // {"error":"no matching provider configured","endpoint":"..."}. Diese
    // Auskunft zu verschlucken heisst, jeden Fehler gleich aussehen zu lassen.
    const payload = err?.response?.data;

    // Der ibe_server reicht bei Provider-Fehlern das GANZE Axios-Fehlerobjekt
    // durch (`res.status(500).send(err)`). Dessen `message` ist nur
    // "Request failed with status code 400" -- die eigentliche Begruendung des
    // Anbieters steckt eine Ebene tiefer in response.data. Nur die oberste
    // Meldung zu zeigen heisst, die Auskunft wegzuwerfen, die man sucht.
    const upstream = payload?.response?.data;
    const asText = (v) =>
      typeof v === "string"
        ? v
        : v && typeof v === "object"
          ? JSON.stringify(v).slice(0, 600)
          : null;

    const detail =
      (typeof payload === "string" && payload) ||
      payload?.error ||
      payload?.msg ||
      asText(upstream) ||
      payload?.message ||
      null;
    const status = err?.response?.status;
    throw new Error(
      detail
        ? `${detail}${status ? ` (HTTP ${status})` : ""}`
        : err?.message || "Die Suche ist fehlgeschlagen."
    );
  }

  // Der pyserver verpackt jede Antwort in StandardResponse ({code,msg,data});
  // der ibe_server antwortet flach. Beides hier entpacken, damit der Aufrufer
  // den Unterschied nicht kennen muss.
  const raw = res.data || {};
  const data = raw.Offers ? raw : raw.data || {};

  // Kein Offers-Block heisst: die Quelle hat nicht geantwortet wie erwartet.
  // Das als "nichts gefunden" zu zeigen waere die Falle aus der Uebergabe.
  if (!data.Offers) {
    throw new Error("Der Anbieter hat keine verwertbare Antwort geliefert.");
  }

  return {
    offers: Array.isArray(data.Offers.Offer) ? data.Offers.Offer : [],
    hotels: data.HotelDictionary?.Hotel || [],
    geo: data.GeoDictionary || null,
  };
}

/**
 * Aus einem Angebot einen Baustein machen.
 *
 * Die Grundangaben sind je Modus andere -- deshalb zwei Bausteintypen und nicht
 * ein Typ mit Verzweigung. Genau darum geht es im Bausteinmodell.
 */
export function offerToStayPart(offer, hotels, mode, anchor, geo) {
  const acc = offer?.OfferServices?.Package?.Accommodation;
  const hotelId = acc?.HotelRef?.HotelID;
  // Lose Gleichheit wie im uebrigen Client (reducers/api.js): die IDs kommen je
  // nach Provider als Zahl oder als Text.
  const hotel = hotels.find((h) => h.HotelID == hotelId) || null;

  const stars = Number(hotel?.HotelCategory) || null;
  const board = acc?.BoardType || "";
  const nights = offer?.TravelDateInfo?.OvernightStays ?? null;

  // Preis wie geliefert: pro Person fuer den Aufenthalt. Nicht auf Naechte
  // heruntergerechnet -- siehe Kopf dieser Datei.
  const amount = Number(offer?.PriceInfo?.Price?.value) || 0;
  const currency = offer?.PriceInfo?.Price?.currency || "EUR";

  const lines = [];
  if (stars) lines.push(`${stars} Sterne`);
  // Verpflegung im Klartext. Der Anbieter liefert Schluessel wie
  // "HalfBoardSuperior" -- die gehoeren in den Code, nicht auf die Karte.
  if (board) lines.push(boardLabel(board));

  if (mode === STAY_MODES.PACKAGE) {
    const flight = offer?.OfferServices?.Package?.Flight;
    const out = Array.isArray(flight) ? flight[0] : flight;
    const from = out?.OutboundFlight?.FlightDeparture?.DepartureAirportRef?.AirportCode;
    if (from) lines.push(`Flug ab ${from}`);
    if (nights != null) lines.push(`${nights} Nächte`);
  } else {
    const km = venueDistance(hotel, anchor);
    if (km != null) lines.push(`${km.toFixed(1)} km zur Venue`);
    if (nights != null) lines.push(`${nights} Nächte`);
  }

  return {
    type: mode === STAY_MODES.PACKAGE ? PART_TYPES.PACKAGE : PART_TYPES.HOTEL,
    id: `${mode}:${offer?.OfferID || hotelId || Math.random().toString(36).slice(2)}`,
    // Das Verzeichnis nennt das Feld "Name" -- NICHT "HotelName". Stand hier
    // vorher falsch, mit dem Ergebnis, dass jede Karte "Unterkunft" hiess.
    title: hotel?.Name || acc?.HotelRef?.HotelName || "Unterkunft",
    // Hoechstens drei Grundangaben -- die vierte gehoert in die Detailansicht.
    lines: lines.slice(0, 3),
    description: "",
    media: {
      // Bildfeld wie im uebrigen Client (SectionCard, MapView).
      imageUrl: hotel?.HotelImages?.LargeImage || hotel?.HotelImages?.SmallImage || null,
      badge: mode === STAY_MODES.PACKAGE ? "Pauschalreise" : "Unterkunft",
      // Stadt aus dem Geo-Verzeichnis; die Bestandssuche des pyservers fuehrt
      // keins, liefert aber die Region am Haus.
      caption: cityName(hotel, geo) || hotel?.HotelRegion || "",
    },
    // 0 heisst "kein Preis geliefert" -> auf Anfrage, niemals ein Nullpreis.
    price: amount > 0
      ? { amount, currency, unit: PRICE_UNITS.PER_PERSON, from: false }
      : null,
    provider: offer?.TourOperator?.Name || (mode === STAY_MODES.PACKAGE ? "Veranstalter" : "Hotelanbieter"),
    source: mode,
    sourceId: String(hotelId || ""),
    anchorData: {},
    constraints: [],
    // Rohdaten fuer den Buchungsschritt -- der Baustein darf nicht die einzige
    // Spur des Angebots sein.
    raw: { offerId: offer?.OfferID || null, hotelId: hotelId || null },
    // Fuer die Karte: Sterne, Verpflegung und Entfernung einzeln, nicht nur als
    // Textzeile -- die Karte ordnet sie anders an als die Liste.
    stay: {
      // Der Rohsatz aus dem Hotelverzeichnis. Die Karte braucht ihn fuer die
      // Bildergalerie (ueber die GIATA-ID) und fuer die Hotelinfos -- das
      // Detail-Popup erwartet genau dieses Objekt.
      hotel,
      giataId: hotel?.HotelCodes?.HotelGiataID || null,
      stars,
      board,
      boardLabel: board ? boardLabel(board) : "",
      nights,
      distanceKm: mode === STAY_MODES.PACKAGE ? null : venueDistance(hotel, anchor),
    },
  };
}

/**
 * Verpflegungsschluessel des Anbieters in die Bezeichnung uebersetzen, die im
 * uebrigen Client steht. Unbekanntes bleibt unveraendert stehen -- lieber der
 * rohe Schluessel als eine erfundene Bezeichnung.
 */
export function boardLabel(key) {
  const hit = boardTypes.find((b) => b.value === key);
  return hit ? hit.label : key;
}

/** Stadtname aus dem Geo-Verzeichnis, ueber die CityID des Hotels. */
function cityName(hotel, geo) {
  const cityId = hotel?.HotelLocationRef?.CityID;
  if (!cityId || !geo) return "";
  const city = geo?.Cities?.City?.find((c) => c.CityID == cityId);
  return city?.Name || "";
}

/** Entfernung Hotel -> Venue, sofern beide Koordinaten haben. */
function venueDistance(hotel, anchor) {
  // Koordinatenfeld wie im uebrigen Client (MapView): HotelGeoPoint.
  const hLat = Number(hotel?.HotelGeoPoint?.Latitude);
  const hLng = Number(hotel?.HotelGeoPoint?.Longitude);
  const vLat = Number(anchor?.anchorData?.lat);
  const vLng = Number(anchor?.anchorData?.lng);
  if (![hLat, hLng, vLat, vLng].every(Number.isFinite)) return null;
  return distanceKm(vLat, vLng, hLat, hLng);
}
